import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { format, parse, parseISO } from "date-fns";
import { fromZonedTime } from "date-fns-tz";
import { PanelRightClose, X, GripVertical } from "lucide-react";
import { useFiles } from "../../hooks/useFiles";
import { useIsMobile, useIsTablet } from "../../hooks/useMobile";
import { useAppContext } from "../../contexts/AppContext";
import {
  Button,
  ToggleGroup,
  ToggleGroupItem,
  TimeSpinnerInput,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Input,
  Label,
} from "@exiftrator/shared/components/ui";
import { PanelHeader } from "../PanelHeader";
import { cn } from "../utils/cn";
import {
  WORKSPACE_SPACING,
  getDensityClass,
} from "../Workspace/workspaceSpacing";

// Helper to parse file time into 12-hour format components
const parseFileTime = (dateTaken) => {
  if (!dateTaken) {
    return { ...DEFAULT_TIME };
  }
  const parsed = parseISO(dateTaken);
  const h24 = parseInt(format(parsed, "HH"), 10);
  return {
    hour: String(h24 === 0 ? 12 : h24 > 12 ? h24 - 12 : h24),
    minute: format(parsed, "mm"),
    second: format(parsed, "ss"),
    ampm: h24 >= 12 ? "PM" : "AM",
  };
};

const DEFAULT_TIME = {
  hour: "12",
  minute: "00",
  second: "00",
  ampm: "PM",
};

function TimePickerContent({
  date,
  hour: initialHour,
  timezone,
  files,
  onClose,
  inSheet = false,
}) {
  const { setCalendarPanelExpanded } = useAppContext();
  const { assignFilesDate, assignFilesDatesIndividually } = useFiles();
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();

  // Determine if hour is locked (from weekly view with specific hour)
  const isHourLocked = initialHour !== null && initialHour !== undefined;

  // Calculate initial values from existing timestamps
  const initialValues = useMemo(() => {
    if (files.length === 0) {
      // Empty state - default values
      const defaultHour =
        initialHour !== null && initialHour !== undefined
          ? initialHour === 0
            ? 12
            : initialHour > 12
              ? initialHour - 12
              : initialHour
          : 12;
      const defaultAmpm =
        initialHour !== null && initialHour !== undefined
          ? initialHour >= 12
            ? "PM"
            : "AM"
          : "PM";

      return {
        hour: String(defaultHour),
        minute: "00",
        second: "00",
        ampm: defaultAmpm,
        mode: "same-time",
        autoOffsetSeconds: "1",
        offsetUnit: "minutes",
        fileOrder: [],
        fileTimes: new Map(),
      };
    }

    const filesWithTimestamps = files.filter(
      (f) => f.file.modifiedExifData?.dateTaken,
    );

    // Build fileTimes map for discrete mode
    const fileTimes = new Map(
      files.map((f) => {
        const dateTaken = f.file.modifiedExifData?.dateTaken;
        return [f.index, parseFileTime(dateTaken)];
      }),
    );

    // If hour is provided (from weekly view), use it as base time
    if (initialHour !== null && initialHour !== undefined) {
      const hour12 =
        initialHour === 0
          ? 12
          : initialHour > 12
            ? initialHour - 12
            : initialHour;
      const ampm = initialHour >= 12 ? "PM" : "AM";

      return {
        hour: String(hour12),
        minute: "00",
        second: "00",
        ampm,
        mode: "same-time",
        autoOffsetSeconds: "1",
        offsetUnit: "minutes",
        fileOrder: files.map((f) => f.index),
        fileTimes,
      };
    }

    if (filesWithTimestamps.length === 0) {
      return {
        hour: "12",
        minute: "00",
        second: "00",
        ampm: "PM",
        mode: "same-time",
        autoOffsetSeconds: "1",
        offsetUnit: "minutes",
        fileOrder: files.map((f) => f.index),
        fileTimes,
      };
    }

    // Find the earliest timestamp to use as base time
    const timestamps = filesWithTimestamps
      .map((f) => {
        const timestamp = parseISO(f.file.modifiedExifData.dateTaken);
        return { index: f.index, date: timestamp };
      })
      .sort((a, b) => a.date - b.date);

    const baseTimestamp = timestamps[0].date;

    // Calculate offsets for each file
    const offsetValues = [];
    files.forEach((f) => {
      if (f.file.modifiedExifData?.dateTaken) {
        const fileTimestamp = parseISO(f.file.modifiedExifData.dateTaken);
        const offsetSeconds = Math.floor(
          (fileTimestamp - baseTimestamp) / 1000,
        );
        offsetValues.push(offsetSeconds);
      } else {
        offsetValues.push(0);
      }
    });

    // Detect mode based on timestamps
    let mode = "discrete";
    let autoOffsetSeconds = "1";
    let offsetUnit = "minutes";

    // Check if all files have the same timestamp (same-time mode)
    const allSameTime = offsetValues.every((offset) => offset === 0);
    if (allSameTime) {
      mode = "same-time";
    } else if (offsetValues.length >= 2) {
      // Check for auto-offset (arithmetic sequence)
      const differences = [];
      for (let i = 1; i < offsetValues.length; i++) {
        differences.push(offsetValues[i] - offsetValues[i - 1]);
      }

      const firstDiff = differences[0];
      const isAutoOffset = differences.every(
        (diff) => Math.abs(diff - firstDiff) <= 1,
      );

      if (isAutoOffset && firstDiff > 0) {
        mode = "auto-offset";

        if (firstDiff % 3600 === 0) {
          offsetUnit = "hours";
          autoOffsetSeconds = String(firstDiff / 3600);
        } else if (firstDiff % 60 === 0) {
          offsetUnit = "minutes";
          autoOffsetSeconds = String(firstDiff / 60);
        } else {
          offsetUnit = "seconds";
          autoOffsetSeconds = String(firstDiff);
        }
      }
    }

    // Convert to 12-hour format for display
    const hour24 = parseInt(format(baseTimestamp, "HH"), 10);
    const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
    const ampm = hour24 >= 12 ? "PM" : "AM";

    return {
      hour: String(hour12),
      minute: format(baseTimestamp, "mm"),
      second: format(baseTimestamp, "ss"),
      ampm,
      mode,
      autoOffsetSeconds,
      offsetUnit,
      fileOrder: files.map((f) => f.index),
      fileTimes,
    };
  }, [files, initialHour]);

  const [hour, setHour] = useState(initialValues.hour);
  const [minute, setMinute] = useState(initialValues.minute);
  const [second, setSecond] = useState(initialValues.second);
  const [ampm, setAmpm] = useState(initialValues.ampm);
  const [mode, setMode] = useState(initialValues.mode);
  const [autoOffsetSeconds, setAutoOffsetSeconds] = useState(
    initialValues.autoOffsetSeconds,
  );
  const [offsetUnit, setOffsetUnit] = useState(initialValues.offsetUnit);
  const [fileOrder, setFileOrder] = useState(initialValues.fileOrder);
  const [fileTimes, setFileTimes] = useState(initialValues.fileTimes);
  const [draggedIndex, setDraggedIndex] = useState(null);

  // Track last applied signature to prevent duplicate applications
  const lastAppliedRef = useRef(null);

  // Sync fileOrder and fileTimes when files change
  useEffect(() => {
    if (files.length === 0) return;

    const currentFileIndices = new Set(files.map((f) => f.index));
    const updatedOrder = fileOrder.filter((idx) => currentFileIndices.has(idx));

    // Add any new files that aren't in the order
    files.forEach((f) => {
      if (!updatedOrder.includes(f.index)) {
        updatedOrder.push(f.index);
      }
    });

    if (
      updatedOrder.length !== fileOrder.length ||
      !updatedOrder.every((idx, i) => fileOrder[i] === idx)
    ) {
      setFileOrder(updatedOrder);
    }

    // Update fileTimes for any new files
    const newFileTimes = new Map(fileTimes);
    let fileTimesChanged = false;
    files.forEach((f) => {
      if (!newFileTimes.has(f.index)) {
        const dateTaken = f.file.modifiedExifData?.dateTaken;
        newFileTimes.set(f.index, parseFileTime(dateTaken));
        fileTimesChanged = true;
      }
    });
    // Remove files that no longer exist
    for (const idx of newFileTimes.keys()) {
      if (!currentFileIndices.has(idx)) {
        newFileTimes.delete(idx);
        fileTimesChanged = true;
      }
    }
    if (fileTimesChanged) {
      setFileTimes(newFileTimes);
    }
  }, [files, fileOrder, fileTimes]);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  const convertToSeconds = (value, unit) => {
    const num = parseFloat(value) || 0;
    if (unit === "hours") return num * 3600;
    if (unit === "minutes") return num * 60;
    return num;
  };

  const isTimeValueValid = (value, min, max) => {
    if (value === "") return false;
    const num = parseInt(value, 10);
    return !isNaN(num) && num >= min && num <= max;
  };

  const isOffsetValueValid = (value, min) => {
    if (value === "") return false;
    const num = parseFloat(value);
    return !isNaN(num) && num >= min;
  };

  const isHourValid = isTimeValueValid(hour, 1, 12);
  const isMinuteValid = isTimeValueValid(minute, 0, 59);
  const isSecondValid = isTimeValueValid(second, 0, 59);
  const isAutoOffsetValid =
    mode !== "auto-offset" || isOffsetValueValid(autoOffsetSeconds, 0.001);

  const isBaseTimeValid =
    mode === "discrete" || (isHourValid && isMinuteValid && isSecondValid);

  const areFileTimesValid =
    mode !== "discrete" ||
    fileOrder.every((idx) => {
      const t = fileTimes.get(idx);
      return (
        isTimeValueValid(t?.hour, 1, 12) &&
        isTimeValueValid(t?.minute, 0, 59) &&
        isTimeValueValid(t?.second, 0, 59)
      );
    });

  const isFormValid =
    files.length > 0 &&
    isBaseTimeValid &&
    areFileTimesValid &&
    isAutoOffsetValid;

  const calculateFinalTimestamp = (baseDate, offsetSeconds) => {
    const finalDate = new Date(baseDate);
    finalDate.setSeconds(finalDate.getSeconds() + offsetSeconds);
    return finalDate;
  };

  const getDisplayTimestamp = (fileIndex, position) => {
    const parsedDate = parse(date, "yyyy-MM-dd", new Date());

    if (mode === "discrete") {
      const t = fileTimes.get(fileIndex);
      if (!t) return "--:--:--";

      const h12 = parseInt(t.hour, 10) || 0;
      const m = parseInt(t.minute, 10) || 0;
      const s = parseInt(t.second, 10) || 0;

      if (isNaN(h12) || isNaN(m) || isNaN(s)) {
        return "--:--:--";
      }

      let h24 = h12;
      if (t.ampm === "AM") {
        if (h12 === 12) h24 = 0;
      } else {
        if (h12 !== 12) h24 = h12 + 12;
      }

      parsedDate.setHours(h24, m, s);
      return format(parsedDate, "HH:mm:ss");
    }

    const h12 = parseInt(hour, 10) || 0;
    const m = parseInt(minute, 10) || 0;
    const s = parseInt(second, 10) || 0;

    if (isNaN(h12) || isNaN(m) || isNaN(s)) {
      return "--:--:--";
    }

    let h24 = h12;
    if (ampm === "AM") {
      if (h12 === 12) h24 = 0;
    } else {
      if (h12 !== 12) h24 = h12 + 12;
    }

    parsedDate.setHours(h24, m, s);

    if (mode === "same-time") {
      return format(parsedDate, "HH:mm:ss");
    }

    const autoOffset = parseFloat(autoOffsetSeconds);
    if (isNaN(autoOffset)) {
      return "--:--:--";
    }
    const offset = position * convertToSeconds(autoOffset, offsetUnit);
    const finalTimestamp = calculateFinalTimestamp(parsedDate, offset);
    return format(finalTimestamp, "HH:mm:ss");
  };

  const handleDragStart = (e, position) => {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", position.toString());
    setDraggedIndex(position);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e, targetPosition) => {
    e.preventDefault();
    const sourcePosition = parseInt(e.dataTransfer.getData("text/plain"), 10);

    if (sourcePosition === targetPosition) return;

    const newOrder = [...fileOrder];
    const [movedItem] = newOrder.splice(sourcePosition, 1);
    newOrder.splice(targetPosition, 0, movedItem);
    setFileOrder(newOrder);
    setDraggedIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  // Apply time changes to files
  const applyTimeChanges = useCallback((overrides = {}) => {
    const nextMode = overrides.mode ?? mode;
    const nextBaseTime = overrides.baseTime ?? { hour, minute, second, ampm };
    const nextFileTimes = overrides.fileTimes ?? fileTimes;
    const nextAutoOffsetSeconds =
      overrides.autoOffsetSeconds ?? autoOffsetSeconds;
    const nextOffsetUnit = overrides.offsetUnit ?? offsetUnit;

    const nextIsHourValid = isTimeValueValid(nextBaseTime.hour, 1, 12);
    const nextIsMinuteValid = isTimeValueValid(nextBaseTime.minute, 0, 59);
    const nextIsSecondValid = isTimeValueValid(nextBaseTime.second, 0, 59);
    const nextIsBaseTimeValid =
      nextMode === "discrete" ||
      (nextIsHourValid && nextIsMinuteValid && nextIsSecondValid);
    const nextAreFileTimesValid =
      nextMode !== "discrete" ||
      fileOrder.every((idx) => {
        const t = nextFileTimes.get(idx);
        return (
          isTimeValueValid(t?.hour, 1, 12) &&
          isTimeValueValid(t?.minute, 0, 59) &&
          isTimeValueValid(t?.second, 0, 59)
        );
      });
    const nextIsAutoOffsetValid =
      nextMode !== "auto-offset" ||
      isOffsetValueValid(nextAutoOffsetSeconds, 0.001);

    if (
      files.length === 0 ||
      !nextIsBaseTimeValid ||
      !nextAreFileTimesValid ||
      !nextIsAutoOffsetValid
    ) {
      return;
    }

    // Create a signature to detect if we already applied these values
    const signature = JSON.stringify({
      date,
      mode: nextMode,
      hour: nextBaseTime.hour,
      minute: nextBaseTime.minute,
      second: nextBaseTime.second,
      ampm: nextBaseTime.ampm,
      autoOffsetSeconds: nextAutoOffsetSeconds,
      offsetUnit: nextOffsetUnit,
      fileOrder,
      fileTimes: Array.from(nextFileTimes.entries()),
    });

    if (signature === lastAppliedRef.current) return;
    lastAppliedRef.current = signature;

    const baseParsedDate = parse(date, "yyyy-MM-dd", new Date());

    if (nextMode === "discrete") {
      const fileIndicesWithDates = fileOrder.map((fileIndex) => {
        const t = nextFileTimes.get(fileIndex);
        const h12 = parseInt(t.hour, 10);
        let h24 = h12;
        if (t.ampm === "AM") {
          if (h12 === 12) h24 = 0;
        } else {
          if (h12 !== 12) h24 = h12 + 12;
        }

        const fileDate = parse(date, "yyyy-MM-dd", new Date());
        fileDate.setHours(h24, parseInt(t.minute, 10), parseInt(t.second, 10));
        const zonedFileDate = fromZonedTime(fileDate, timezone);

        return { index: fileIndex, date: zonedFileDate };
      });

      assignFilesDatesIndividually(fileIndicesWithDates);
      return;
    }

    let h12 = parseInt(nextBaseTime.hour, 10);
    let h24 = h12;
    if (nextBaseTime.ampm === "AM") {
      if (h12 === 12) h24 = 0;
    } else {
      if (h12 !== 12) h24 = h12 + 12;
    }

    baseParsedDate.setHours(
      h24,
      parseInt(nextBaseTime.minute, 10),
      parseInt(nextBaseTime.second, 10),
    );
    const zonedDate = fromZonedTime(baseParsedDate, timezone);

    if (nextMode === "same-time") {
      assignFilesDate(fileOrder, zonedDate);
      return;
    }

    const fileIndicesWithDates = fileOrder.map((fileIndex, position) => {
      const offset =
        position *
        convertToSeconds(parseFloat(nextAutoOffsetSeconds), nextOffsetUnit);
      const finalDate = new Date(zonedDate);
      finalDate.setSeconds(finalDate.getSeconds() + offset);
      return { index: fileIndex, date: finalDate };
    });

    assignFilesDatesIndividually(fileIndicesWithDates);
  }, [
    isFormValid,
    files.length,
    date,
    mode,
    hour,
    minute,
    second,
    ampm,
    autoOffsetSeconds,
    offsetUnit,
    fileOrder,
    fileTimes,
    timezone,
    assignFilesDate,
    assignFilesDatesIndividually,
  ]);

  // Auto-apply on blur (when input loses focus)
  const handleBlur = useCallback(() => {
    applyTimeChanges();
  }, [applyTimeChanges]);

  // Auto-apply when file order changes (after drag)
  useEffect(() => {
    if (draggedIndex === null && files.length > 0) {
      applyTimeChanges();
    }
  }, [fileOrder, draggedIndex, files.length, applyTimeChanges]);

  // Apply on mode or offset unit change
  useEffect(() => {
    if (files.length > 0) {
      applyTimeChanges();
    }
  }, [mode, offsetUnit, files.length, applyTimeChanges]);

  const handleFileTimeChange = (fileIndex, value) => {
    const newFileTimes = new Map(fileTimes);
    newFileTimes.set(fileIndex, value);
    setFileTimes(newFileTimes);
  };

  const handleTimeChange = (value) => {
    setHour(value.hour);
    setMinute(value.minute);
    setSecond(value.second);
    setAmpm(value.ampm);
  };

  const handleBaseTimeBlur = useCallback(
    (value) => {
      setHour(value.hour);
      setMinute(value.minute);
      setSecond(value.second);
      setAmpm(value.ampm);
      applyTimeChanges({ baseTime: value });
    },
    [applyTimeChanges],
  );

  const handleFileTimeBlur = useCallback(
    (fileIndex, value) => {
      const newFileTimes = new Map(fileTimes);
      newFileTimes.set(fileIndex, value);
      setFileTimes(newFileTimes);
      applyTimeChanges({ fileTimes: newFileTimes });
    },
    [applyTimeChanges, fileTimes],
  );

  const currentBaseTime = {
    hour,
    minute,
    second,
    ampm,
  };

  const handleRemoveFromDate = (fileIndex) => {
    assignFilesDate([fileIndex], null);
  };

  const handleCollapse = () => {
    setCalendarPanelExpanded(false);
    onClose();
  };

  const parsedDate = parse(date, "yyyy-MM-dd", new Date());
  let formattedDate = format(parsedDate, "EEE, MMM d, yyyy");

  if (initialHour !== null && initialHour !== undefined) {
    const h = parseInt(initialHour, 10);
    const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    const ampmStr = h >= 12 ? "PM" : "AM";
    formattedDate = `${formattedDate} at ${hour12}:00 ${ampmStr}`;
  }

  const containerClassName = inSheet
    ? "w-full bg-bg-secondary flex h-full min-h-0 flex-col overflow-hidden"
    : "w-[320px] bg-bg-secondary border-l border-border-default flex flex-col overflow-hidden";
  const paneInsetXClass = inSheet
    ? WORKSPACE_SPACING.paneInsetX.mobile
    : getDensityClass(isMobile, isTablet, WORKSPACE_SPACING.paneInsetX);
  const paneInsetYClass = inSheet
    ? WORKSPACE_SPACING.paneInsetY.mobile
    : getDensityClass(isMobile, isTablet, WORKSPACE_SPACING.paneInsetY);

  // Empty state
  if (files.length === 0) {
    return (
      <aside className={containerClassName}>
        <PanelHeader title="Date/Time">
          <Button
            size="icon"
            variant="ghost"
            onClick={handleCollapse}
            className="h-8 w-8"
            title={inSheet ? "Close panel" : "Hide date/time panel"}
          >
            {inSheet ? (
              <X data-icon="inline-start" className="h-4 w-4" />
            ) : (
              <PanelRightClose data-icon="inline-start" className="h-4 w-4" />
            )}
          </Button>
        </PanelHeader>

        <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
          <div className="text-lg font-semibold text-text-primary mb-2">
            {formattedDate}
          </div>
          <p className="text-sm text-text-secondary">No files on this date.</p>
          <p className="text-sm text-text-tertiary mt-1">
            Drag files from the sidebar to assign them to this date.
          </p>
        </div>
      </aside>
    );
  }

  return (
    <aside className={containerClassName}>
      <PanelHeader title="Date/Time">
        <Button
          size="icon"
          variant="ghost"
          onClick={handleCollapse}
          className="h-8 w-8"
          title={inSheet ? "Close panel" : "Hide date/time panel"}
        >
          {inSheet ? (
            <X data-icon="inline-start" className="h-4 w-4" />
          ) : (
            <PanelRightClose data-icon="inline-start" className="h-4 w-4" />
          )}
        </Button>
      </PanelHeader>

      <div
        className={cn(
          "flex-1 overflow-y-auto",
          WORKSPACE_SPACING.sectionGapPrimary,
          paneInsetXClass,
          paneInsetYClass,
        )}
      >
        {/* Selected Date */}
        <div className="text-base font-semibold text-text-primary">
          {formattedDate}
        </div>

        {/* Mode Toggle */}
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-text-secondary mb-2 block">
            Mode
          </Label>
          <ToggleGroup
            type="single"
            value={mode}
            onValueChange={(value) => value && setMode(value)}
            variant="outline"
          >
            <ToggleGroupItem value="same-time" className="flex-1 text-xs">
              Same Time
            </ToggleGroupItem>
            <ToggleGroupItem value="auto-offset" className="flex-1 text-xs">
              Auto Offset
            </ToggleGroupItem>
            <ToggleGroupItem value="discrete" className="flex-1 text-xs">
              Discrete
            </ToggleGroupItem>
          </ToggleGroup>
          <p className="text-xs text-text-secondary leading-relaxed mt-2">
            {mode === "same-time" && (
              <>All files will be assigned the exact same timestamp.</>
            )}
            {mode === "auto-offset" && (
              <>
                Files will be spaced evenly with a fixed time interval between
                each photo.
              </>
            )}
            {mode === "discrete" && (
              <>
                Each file will have its own unique timestamp that you can set
                individually.
              </>
            )}
          </p>

          {mode === "auto-offset" && (
            <div className="mt-3 p-3 bg-bg-tertiary/50 border border-border-subtle rounded-md">
              <span className="text-xs text-text-secondary block mb-2">
                Offset between photos:
              </span>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min="1"
                  value={autoOffsetSeconds}
                  onChange={(e) => setAutoOffsetSeconds(e.target.value)}
                  onBlur={handleBlur}
                  className={`w-20 h-8 text-sm text-center ${!isAutoOffsetValid ? "border-accent-danger/50" : ""}`}
                />
                <Select value={offsetUnit} onValueChange={setOffsetUnit}>
                  <SelectTrigger size="sm" className="w-28 h-8 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="seconds">Seconds</SelectItem>
                    <SelectItem value="minutes">Minutes</SelectItem>
                    <SelectItem value="hours">Hours</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </div>

        {/* Base Time Inputs */}
        {(mode === "same-time" || mode === "auto-offset") && (
          <div className="p-3 bg-bg-tertiary/30 border border-border-subtle rounded-md">
            <Label className="text-xs font-semibold uppercase tracking-wider text-text-secondary mb-2 block">
              Base Time
            </Label>
            <TimeSpinnerInput
              value={currentBaseTime}
              size="compact"
              onChange={handleTimeChange}
              onBlur={handleBaseTimeBlur}
              invalidFields={{
                hour: !isHourValid,
                minute: !isMinuteValid,
                second: !isSecondValid,
              }}
              disabledFields={{
                hour: isHourLocked,
                ampm: isHourLocked,
              }}
            />
          </div>
        )}

        {/* Files List */}
        <div>
          <h5 className="text-xs font-bold text-text-primary mb-3 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-accent-primary"></span>
            Files ({files.length})
          </h5>
          <div className="space-y-2">
            {fileOrder.map((fileIndex, position) => {
              const fileData = files.find((f) => f.index === fileIndex);
              if (!fileData) return null;

              const { file } = fileData;
              const thumbnailUrl = file.thumbnailUrl;
              const displayTimestamp = getDisplayTimestamp(fileIndex, position);
              const isDragging = draggedIndex === position;
              const fileTime = fileTimes.get(fileIndex) || DEFAULT_TIME;
              const isFileHourValid = isTimeValueValid(fileTime.hour, 1, 12);
              const isFileMinuteValid = isTimeValueValid(
                fileTime.minute,
                0,
                59,
              );
              const isFileSecondValid = isTimeValueValid(
                fileTime.second,
                0,
                59,
              );

              return (
                <div
                  key={fileIndex}
                  className={`flex flex-col gap-2 p-2 bg-bg-tertiary/50 border border-border-subtle rounded-md group transition-all hover:bg-bg-tertiary hover:border-border-default ${isDragging ? "opacity-30" : ""}`}
                  draggable
                  onDragStart={(e) => handleDragStart(e, position)}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, position)}
                  onDragEnd={handleDragEnd}
                >
                  <div className="flex items-center gap-2">
                    <GripVertical className="w-4 h-4 cursor-move text-text-tertiary hover:text-text-primary" />
                    <img
                      src={thumbnailUrl}
                      alt={file.name}
                      className="w-10 h-10 object-cover rounded-sm border border-border-default"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-text-primary font-medium truncate">
                        {file.name}
                      </div>
                      <div className="text-xs text-text-tertiary font-mono">
                        {displayTimestamp}
                      </div>
                    </div>
                    {mode === "auto-offset" && (
                      <div className="text-xs font-mono text-text-accent bg-bg-selected px-2 py-0.5 rounded border border-accent-primary/20">
                        +{position * (parseFloat(autoOffsetSeconds) || 0)}
                        {offsetUnit === "seconds"
                          ? "s"
                          : offsetUnit === "minutes"
                            ? "m"
                            : "h"}
                      </div>
                    )}
                    <Button
                      size="icon"
                      variant="ghost"
                      className="w-6 h-6 text-text-tertiary hover:bg-accent-danger-glow hover:text-accent-danger"
                      onClick={() => handleRemoveFromDate(fileIndex)}
                      title="Remove from this date"
                    >
                      <X data-icon="inline-start" className="w-4 h-4" />
                    </Button>
                  </div>

                  {mode === "discrete" && (
                    <div className="ml-6">
                      <TimeSpinnerInput
                        value={fileTime}
                        size="compact"
                        onChange={(value) => handleFileTimeChange(fileIndex, value)}
                        onBlur={(value) => handleFileTimeBlur(fileIndex, value)}
                        invalidFields={{
                          hour: !isFileHourValid,
                          minute: !isFileMinuteValid,
                          second: !isFileSecondValid,
                        }}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </aside>
  );
}

export default TimePickerContent;
